import { MapObjectProperties } from "@iventis/map-types";
import { Listener } from "@iventis/types";
import lineSplit from "@turf/line-split";
import { point } from "@turf/helpers";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import { Feature, LineString } from "geojson";

export enum SplitLineEvent {
    Start = "Start",
    Move = "Move",
    Cancel = "Cancel",
    AskSplit = "AskSplit",
    Split = "Split",
}

interface ListenerPattern {
    [SplitLineEvent.Move]: ((moveEvent: { lng: number; lat: number } | undefined) => void)[];
    [SplitLineEvent.Start]: (() => void)[];
    [SplitLineEvent.Cancel]: (() => void)[];
    [SplitLineEvent.AskSplit]: (() => void)[];
    [SplitLineEvent.Split]: ((features: Feature<LineString, MapObjectProperties>[]) => void)[];
}

export class SplitLineHandler {
    private listeners: ListenerPattern = {
        [SplitLineEvent.Move]: [],
        [SplitLineEvent.Start]: [],
        [SplitLineEvent.Cancel]: [],
        [SplitLineEvent.AskSplit]: [],
        [SplitLineEvent.Split]: [],
    };

    private readonly onClickHandler: Listener;

    private readonly onMouseMoveHandler: Listener;

    private readonly onKeyDownHandler: Listener;

    private readonly thresholdToShowSplitIndicatorPx = 40;

    private splitFeatures: Feature<LineString, MapObjectProperties>[] = [];

    constructor(
        private selectedMapObject: Feature<LineString, MapObjectProperties>,
        mapEngineEvents: {
            onClick(callback: (event: { lng: number; lat: number }) => void): Listener;
            onMouseMove: (callback: (event: { lng: number; lat: number }) => void) => Listener;
            onKeyDown: (callback: (event: KeyboardEvent) => void) => Listener;
        },
        private project: (event: { lng: number; lat: number }) => [number, number]
    ) {
        this.onClickHandler = mapEngineEvents.onClick(this.handleOnClick.bind(this));
        this.onMouseMoveHandler = mapEngineEvents.onMouseMove(this.handleOnMouseMove.bind(this));
        this.onKeyDownHandler = mapEngineEvents.onKeyDown(this.handleOnKeyDown.bind(this));

        this.listeners[SplitLineEvent.Start].forEach((listener) => listener());
    }

    /** User has clicked the map to split the line */
    private handleOnClick(event: { lng: number; lat: number }) {
        const pointOnTheLine = this.getNearestPointOnTheLine(event);
        if (pointOnTheLine != null) {
            // Split the line into two, at the point the user has clicked
            const lineFeatureCollection = lineSplit(this.selectedMapObject, point([pointOnTheLine.lng, pointOnTheLine.lat]));
            this.splitFeatures = lineFeatureCollection.features.map((f) => ({
                ...f,
                properties: {
                    ...this.selectedMapObject.properties,
                    id: crypto.randomUUID(),
                },
            }));
            this.listeners[SplitLineEvent.AskSplit].forEach((listener) => listener());
        } else {
            this.listeners[SplitLineEvent.Cancel].forEach((listener) => listener());
        }
    }

    /** On mouse move, ensure we are emitting the point we are nearest to on the line */
    private handleOnMouseMove(event: { lng: number; lat: number }) {
        const nearestPoint = this.getNearestPointOnTheLine(event);
        this.listeners[SplitLineEvent.Move].forEach((listener) => listener(nearestPoint));
    }

    /** If key down is escape, want to cancel splitting the line */
    private handleOnKeyDown(event: KeyboardEvent) {
        if (event.key === "Escape") {
            this.listeners[SplitLineEvent.Cancel].forEach((listener) => listener());
        }
    }

    /** Get the nearest point on the line, if given lng and lat is more than thresholdToShowSplitIndicatorPx away return null  */
    private getNearestPointOnTheLine({ lng, lat }: { lng: number; lat: number } | undefined) {
        const pointOnTheLine = nearestPointOnLine(this.selectedMapObject, point([lng, lat]));
        const [lineLng, lineLat] = pointOnTheLine.geometry.coordinates;
        // If pointOnTheLine is smaller than thresholdToShowSplitIndicatorPx away from given lng lat
        if (this.getDistanceToLine({ lng, lat }, { lng: lineLng, lat: lineLat }) < this.thresholdToShowSplitIndicatorPx) {
            return { lng: lineLng, lat: lineLat };
        }
        return undefined;
    }

    /** Given a point on the line and the user's postion, calculate the amount of pixels between the two positions */
    private getDistanceToLine(pointOnLine: { lng: number; lat: number }, userPosition: { lng: number; lat: number }) {
        const [projectedPointOnLineX, projectedPointOnLineY] = this.project(pointOnLine);
        const [projectedUserPositionX, projectedUserPositionY] = this.project(userPosition);
        // Use Pythagorean theorem to work out the distance - (a * a) + (b * b) = c * c
        return Math.sqrt((projectedUserPositionX - projectedPointOnLineX) ** 2 + (projectedUserPositionY - projectedPointOnLineY) ** 2);
    }

    public confirmSplit() {
        this.listeners[SplitLineEvent.Split].forEach((listener) => listener(this.splitFeatures));
    }

    public on<E extends SplitLineEvent>(event: E, callback: ListenerPattern[keyof ListenerPattern][0]) {
        this.listeners[event].push(callback as () => void);
        return this;
    }

    public remove() {
        this.onClickHandler.remove();
        this.onMouseMoveHandler.remove();
        this.onKeyDownHandler.remove();
    }
}
